'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductOption, ProductVariant, ShopifyImage } from '@repo/shopify';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildProductViewEvent, trackAnalyticsEvent } from '@/lib/analytics/client';
import type { ApparelBundleOffer } from '@/lib/apparel-bundle-config';
import type { HydrateBundleOffer } from '@/lib/insider-bundle-config';
import { toCanonicalPath } from '@/lib/markets';
import type { TrustBadge } from '@/lib/product-page-settings';
import type { ProductPageClientProduct } from '@/lib/product-page-types';
import { recordRecentlyViewedProduct } from '@/lib/recently-viewed';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';
import { useBrowserLocation } from '@/lib/use-browser-location';
import { useLiveAvailability } from './live-availability';
import { resolveProductBadges, salePercentOff } from './product-badges';
import { ProductGallery } from './product-gallery';
import { ProductInfo, type ProductInfoTypeDetails } from './product-info';
import { filterImagesByColor, getColorOption, getSelectedOptionsFromSearchParams, type ProductVariantSelectionLike, resolveSelectedVariant } from './product-option-utils';
import type { SwatchGroupItem } from './product-swatch-group';
import { useUrlVariantSeed } from './use-url-variant-seed';
interface LinkedColorProduct {
  handle: string;
  title: string;
  options: ProductOption[];
  variants: ProductVariantSelectionLike[];
  colorValues: string[];
  colorOptionName: string | null;
}
interface ProductMainProps {
  product: ProductPageClientProduct;
  images: ShopifyImage[];
  variants: ProductVariant[];
  initialSelectedOptions: Record<string, string>;
  siblings: {
    handle: string;
    title: string;
    productType: string;
    featuredImage: ShopifyImage | null;
    colorValues: string[];
    images: ShopifyImage[];
    variants: ProductVariantSelectionLike[];
    discountPercent: number | null;
    keepAcrossColors?: boolean;
  }[];
  linkedColorProducts: LinkedColorProduct[];
  swatchGroup?: SwatchGroupItem[];
  /** Selector heading for the swatch group ("Design" or "Flavor"). */
  swatchGroupLabel?: string;
  /**
   * H1 / sticky-ATC title. Flavor groups use the base product name while
   * Shopify `product.title` stays full-length for cart and analytics.
   */
  displayTitle?: string;
  enableReviews?: boolean;
  moneyLocale?: string;
  /** Star rating from Klaviyo Reviews API */
  rating?: number | null;
  /** Total review count from Klaviyo Reviews API */
  reviewCount?: number | null;
  /** Optional server-streamed review summary */
  reviewSummarySlot?: ReactNode;
  /** Server-rendered CMS sizing chart (SizingChartContent); null hides the link. */
  sizingChartSlot?: ReactNode;
  /** CMS product type details — only the fields the buy box renders. */
  typeDetails?: ProductInfoTypeDetails | null;
  /** CMS-driven Subscribe & Save labels/benefits, resolved for this product */
  subscribeSave?: SubscribeSaveConfig | null;
  /** Insider Bundle offer (config + flavor products); null hides the bundle tab. */
  bundle?: HydrateBundleOffer | null;
  /** Apparel Bundle & Save ladder (cart-driven); null hides the strip. */
  apparelBundle?: ApparelBundleOffer | null;
  /** CMS-driven trust badges, resolved for this product type; null falls back to the built-in pair */
  trustBadges?: TrustBadge[] | null;
  /** Featured review for social proof */
  featuredReview?: {
    author: string;
    content: string;
    rating: number;
    verified: boolean;
  } | null;
  /** Optional server-streamed featured review */
  featuredReviewSlot?: ReactNode;
  children?: ReactNode;
}
export function ProductMain(t0) {
  const $ = _c(70);
  const {
    product,
    images,
    variants,
    initialSelectedOptions,
    siblings,
    linkedColorProducts,
    swatchGroup,
    swatchGroupLabel,
    displayTitle,
    enableReviews: t1,
    moneyLocale,
    rating,
    reviewCount,
    reviewSummarySlot,
    sizingChartSlot,
    typeDetails,
    subscribeSave,
    bundle,
    apparelBundle,
    trustBadges,
    featuredReview,
    featuredReviewSlot,
    children
  } = t0;
  const enableReviews = t1 === undefined ? false : t1;
  const {
    pathname,
    search
  } = useBrowserLocation();
  let t2;
  if ($[0] !== initialSelectedOptions || $[1] !== product.options) {
    t2 = () => {
      const colorOption = getColorOption(product.options);
      return colorOption ? initialSelectedOptions[colorOption.name] ?? null : null;
    };
    $[0] = initialSelectedOptions;
    $[1] = product.options;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [selectedColor, setSelectedColor] = useState(t2);
  const trackedProductUrlRef = useRef(null);
  let t3;
  if ($[3] !== product.options) {
    t3 = seeded => {
      const colorOption_0 = getColorOption(product.options);
      const seededColor = colorOption_0 ? seeded[colorOption_0.name] : undefined;
      if (seededColor) {
        setSelectedColor(seededColor);
      }
    };
    $[3] = product.options;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const applyUrlSeededColor = t3;
  useUrlVariantSeed(variants, product.options, applyUrlSeededColor);
  const {
    availability: resolvedAvailability,
    streaming
  } = useLiveAvailability();
  const availabilityPending = streaming && resolvedAvailability === null;
  let t4;
  bb0: {
    if (!resolvedAvailability || resolvedAvailability.length === 0) {
      let t5;
      if ($[5] !== bundle || $[6] !== swatchGroup || $[7] !== variants) {
        t5 = {
          liveVariants: variants,
          liveSwatchGroup: swatchGroup,
          liveBundle: bundle
        };
        $[5] = bundle;
        $[6] = swatchGroup;
        $[7] = variants;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    let t6;
    let t7;
    if ($[9] !== bundle || $[10] !== resolvedAvailability || $[11] !== swatchGroup || $[12] !== variants) {
      const availabilityById = new Map(resolvedAvailability.map(_temp));
      t5 = variants.map(variant => {
        const live = availabilityById.get(variant.id);
        return live === undefined || live === variant.availableForSale ? variant : {
          ...variant,
          availableForSale: live
        };
      });
      t6 = swatchGroup?.map(item => {
        const live_0 = availabilityById.get(item.productId);
        return live_0 === undefined || live_0 === item.available ? item : {
          ...item,
          available: live_0
        };
      });
      t7 = bundle ? {
        ...bundle,
        flavors: bundle.flavors.map(flavor => {
          const live_1 = availabilityById.get(flavor.productId) ?? availabilityById.get(flavor.merchandiseId);
          return live_1 === undefined || live_1 === flavor.availableForSale ? flavor : {
            ...flavor,
            availableForSale: live_1
          };
        })
      } : bundle;
      $[9] = bundle;
      $[10] = resolvedAvailability;
      $[11] = swatchGroup;
      $[12] = variants;
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
    } else {
      t5 = $[13];
      t6 = $[14];
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
      t8 = {
        liveVariants: t5,
        liveSwatchGroup: t6,
        liveBundle: t7
      };
      $[16] = t5;
      $[17] = t6;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t4 = t8;
  }
  const {
    liveVariants,
    liveSwatchGroup,
    liveBundle
  } = t4;
  let t5;
  if ($[20] !== images || $[21] !== selectedColor) {
    t5 = filterImagesByColor(images, selectedColor);
    $[20] = images;
    $[21] = selectedColor;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const filteredImages = t5;
  const price = product.priceRange.minVariantPrice;
  const compareAtPrice = product.compareAtPriceRange?.minVariantPrice;
  const isOnSale = Boolean(compareAtPrice && Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(price.amount));
  let t6;
  if ($[23] !== availabilityPending || $[24] !== compareAtPrice?.amount || $[25] !== isOnSale || $[26] !== liveVariants || $[27] !== price.amount || $[28] !== product.tags) {
    const availableForSale = availabilityPending || liveVariants.length === 0 || liveVariants.some(_temp2);
    t6 = resolveProductBadges({
      tags: product.tags,
      availableForSale,
      isOnSale,
      percentOff: salePercentOff(price.amount, compareAtPrice?.amount)
    });
    $[23] = availabilityPending;
    $[24] = compareAtPrice?.amount;
    $[25] = isOnSale;
    $[26] = liveVariants;
    $[27] = price.amount;
    $[28] = product.tags;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const imageBadges = t6.imageBadges;
  let t7;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = color => {
      setSelectedColor(color);
    };
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const handleSelectedColorChange = t7;
  let t8;
  let t9;
  if ($[31] !== pathname || $[32] !== product || $[33] !== search || $[34] !== variants) {
    t8 = () => {
      if (!pathname) {
        return;
      }
      const searchParams = new URLSearchParams(search);
      const requestedOptions = getSelectedOptionsFromSearchParams(searchParams, variants, product.options);
      const trackedVariant = resolveSelectedVariant(variants, requestedOptions) || variants[0] || null;
      const canonicalPathname = toCanonicalPath(pathname);
      if (trackedProductUrlRef.current === canonicalPathname) {
        return;
      }
      trackedProductUrlRef.current = canonicalPathname;
      trackAnalyticsEvent(buildProductViewEvent(product, trackedVariant));
      recordRecentlyViewedProduct(product.handle);
    };
    t9 = [pathname, product, search, variants];
    $[31] = pathname;
    $[32] = product;
    $[33] = search;
    $[34] = variants;
    $[35] = t8;
    $[36] = t9;
  } else {
    t8 = $[35];
    t9 = $[36];
  }
  useEffect(t8, t9);
  let t10;
  if ($[37] !== filteredImages || $[38] !== imageBadges || $[39] !== product.title) {
    t10 = <ProductGallery images={filteredImages} productTitle={product.title} badges={imageBadges} />;
    $[37] = filteredImages;
    $[38] = imageBadges;
    $[39] = product.title;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== apparelBundle || $[42] !== availabilityPending || $[43] !== displayTitle || $[44] !== enableReviews || $[45] !== featuredReview || $[46] !== featuredReviewSlot || $[47] !== initialSelectedOptions || $[48] !== linkedColorProducts || $[49] !== liveBundle || $[50] !== liveSwatchGroup || $[51] !== liveVariants || $[52] !== moneyLocale || $[53] !== product || $[54] !== rating || $[55] !== reviewCount || $[56] !== reviewSummarySlot || $[57] !== siblings || $[58] !== sizingChartSlot || $[59] !== subscribeSave || $[60] !== swatchGroupLabel || $[61] !== trustBadges || $[62] !== typeDetails) {
    t11 = <ProductInfo product={product} variants={liveVariants} availabilityPending={availabilityPending} initialSelectedOptions={initialSelectedOptions} siblings={siblings} linkedColorProducts={linkedColorProducts} swatchGroup={liveSwatchGroup} swatchGroupLabel={swatchGroupLabel} displayTitle={displayTitle} onSelectedColorChange={handleSelectedColorChange} enableReviews={enableReviews} moneyLocale={moneyLocale} rating={rating} reviewCount={reviewCount} reviewSummarySlot={reviewSummarySlot} sizingChartSlot={sizingChartSlot} typeDetails={typeDetails} subscribeSave={subscribeSave} bundle={liveBundle} apparelBundle={apparelBundle} trustBadges={trustBadges} featuredReview={featuredReview} featuredReviewSlot={featuredReviewSlot} />;
    $[41] = apparelBundle;
    $[42] = availabilityPending;
    $[43] = displayTitle;
    $[44] = enableReviews;
    $[45] = featuredReview;
    $[46] = featuredReviewSlot;
    $[47] = initialSelectedOptions;
    $[48] = linkedColorProducts;
    $[49] = liveBundle;
    $[50] = liveSwatchGroup;
    $[51] = liveVariants;
    $[52] = moneyLocale;
    $[53] = product;
    $[54] = rating;
    $[55] = reviewCount;
    $[56] = reviewSummarySlot;
    $[57] = siblings;
    $[58] = sizingChartSlot;
    $[59] = subscribeSave;
    $[60] = swatchGroupLabel;
    $[61] = trustBadges;
    $[62] = typeDetails;
    $[63] = t11;
  } else {
    t11 = $[63];
  }
  let t12;
  if ($[64] !== children || $[65] !== t11) {
    t12 = <div className="lg:sticky lg:top-4 lg:self-start">{t11}{children}</div>;
    $[64] = children;
    $[65] = t11;
    $[66] = t12;
  } else {
    t12 = $[66];
  }
  let t13;
  if ($[67] !== t10 || $[68] !== t12) {
    t13 = <div className="grid gap-4 lg:grid-cols-[1fr_420px] lg:gap-12 xl:grid-cols-[1fr_480px]">{t10}{t12}</div>;
    $[67] = t10;
    $[68] = t12;
    $[69] = t13;
  } else {
    t13 = $[69];
  }
  return t13;
}
function _temp2(variant_0) {
  return variant_0.availableForSale;
}
function _temp(entry) {
  return [entry.id, entry.availableForSale];
}